import { EditorExtensionSDK } from '@contentful/app-sdk';
import cryptoRandomString from 'crypto-random-string';
import * as R from 'ramda';

import {
  ApiChannelEntry,
  ManualEntry,
  ManualEntryCard,
  PaginatedData,
  Pagination,
  TaggingToolEntryBase,
} from '../types';
import fetchFromAppApi from './app-api/fetch-from-app-api';
import { ENTRY_TYPES_DISPLAY_NAMES, MAX_PER_PAGE } from './constants';
import fetchWithCatch from './fetch-with-catch';
import {
  getContentfulContentTypeDisplayName,
  getContentfulEntry,
  getPublishState,
} from './get-from-contentful';
import { isCmsEntryType, isTaggingToolEntryType, notNull } from './utils';

const getContentfulManualEntryCard = async (
  manualEntry: ManualEntry,
  sdk: EditorExtensionSDK,
): Promise<ManualEntryCard> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const contentfulEntry: any = await getContentfulEntry(
    sdk,
    manualEntry.entryId,
  );

  return {
    id: manualEntry.id,
    entryId: manualEntry.entryId,
    type: manualEntry.type,
    typeDisplayName: await getContentfulContentTypeDisplayName(
      sdk,
      contentfulEntry.sys.contentType.sys.id,
    ),
    title: contentfulEntry.fields.title
      ? contentfulEntry.fields.title['de-CH']
      : undefined,
    publishState: getPublishState(contentfulEntry),
    position: manualEntry.position,
  } as ManualEntryCard;
};

const mapToManualEntryCard = (
  taggingToolEntry: TaggingToolEntryBase,
  manualEntries: ManualEntry[],
): ManualEntryCard | null => {
  const taggingId = String(taggingToolEntry.id);
  const currentManualEntry = manualEntries.find(
    (e) => String(e.entryId) === taggingId,
  );

  const {
    id: currentManualEntryId,
    entryId: currentManualEntryEntryId,
    type: currentManualEntryType,
    position: currentManualPosition,
  } = currentManualEntry ?? {};

  if (
    !currentManualEntryId ||
    !currentManualEntryType ||
    !currentManualEntryEntryId
  ) {
    console.warn('Could not resolve manual entry type.');
    return null;
  }

  return {
    id: currentManualEntryId,
    // Keep the original stored entryId shape to match downstream consumers.
    entryId: currentManualEntryEntryId,
    type: currentManualEntryType,
    typeDisplayName: ENTRY_TYPES_DISPLAY_NAMES[currentManualEntryType],
    title: taggingToolEntry.title || currentManualEntryType,
    description: taggingToolEntry.description,
    thumbnailUrl: taggingToolEntry.imageUrl,
    position: currentManualPosition,
  };
};

const mapToFallbackManualEntryCard = (
  manualEntry: ManualEntry,
): ManualEntryCard => ({
  id: manualEntry.id,
  entryId: manualEntry.entryId,
  type: manualEntry.type,
  typeDisplayName: ENTRY_TYPES_DISPLAY_NAMES[manualEntry.type],
  title: manualEntry.type,
  description: manualEntry.entryId,
  position: manualEntry.position,
});

const MAX_FLEXIBLE_ENTITIES_PAGES_TO_LOAD = 50;

const buildFlexibleEntitiesPath = (entryIds: string[], page: number): string =>
  `/flexibleentities?id=${entryIds.join(
    ',',
  )}&per_page=${MAX_PER_PAGE}&page=${page}`;

type FlexibleEntityPageResult = {
  rows: TaggingToolEntryBase[];
  pagination: Pagination | null;
};

const fetchFlexibleEntityPage = async (
  entryIds: string[],
  page: number,
): Promise<FlexibleEntityPageResult | null> => {
  const { data, error } = await fetchWithCatch<
    PaginatedData<TaggingToolEntryBase[]>
  >(() => fetchFromAppApi(buildFlexibleEntitiesPath(entryIds, page)));

  if (error) {
    console.error(error);
  }

  if (error || !data) {
    return null;
  }

  return {
    rows: data.data ?? [],
    pagination: data.pagination,
  };
};

const deduplicateById = (
  rows: TaggingToolEntryBase[],
): TaggingToolEntryBase[] => {
  const byId = new Map<string, TaggingToolEntryBase>();
  for (const row of rows) {
    byId.set(String(row.id), row);
  }

  return Array.from(byId.values());
};

const fetchAllFlexibleEntityRows = async (
  entryIds: string[],
): Promise<TaggingToolEntryBase[]> => {
  if (entryIds.length === 0) return [];

  const allRows: TaggingToolEntryBase[] = [];

  for (let page = 1; page <= MAX_FLEXIBLE_ENTITIES_PAGES_TO_LOAD; page += 1) {
    // Sequential requests: page N+1 depends on pagination metadata from page N.
    // eslint-disable-next-line no-await-in-loop
    const result = await fetchFlexibleEntityPage(entryIds, page);
    if (!result) {
      break;
    }

    allRows.push(...result.rows);

    const hasMorePages =
      result.rows.length > 0 &&
      result.pagination != null &&
      allRows.length < result.pagination.total;

    if (!hasMorePages) {
      break;
    }
  }

  return deduplicateById(allRows);
};

const getTaggingToolManualEntryCards = async (
  manualEntries: ManualEntry[],
): Promise<ManualEntryCard[]> => {
  const entryIds = manualEntries.map((entry) => String(entry.entryId));
  const rows = await fetchAllFlexibleEntityRows(entryIds);

  return rows
    .map((taggingToolEntry) =>
      mapToManualEntryCard(taggingToolEntry, manualEntries),
    )
    .filter(notNull);
};

const getChannelManualEntryCards = async (
  manualEntries: ManualEntry[],
): Promise<ManualEntryCard[]> => {
  const entryIds = manualEntries.map((entry) => entry.entryId);

  if (entryIds.length === 0) {
    return [];
  }

  const { data, error } = await fetchWithCatch<
    PaginatedData<ApiChannelEntry[]>
  >(() => fetchFromAppApi(`/channels`));

  if (error) {
    console.error(error);
    return [];
  }

  const manualChannelEntries = (data?.data.map((channel) => ({
    id: channel.id,
    title: channel.name,
    imageUrl: channel.logo_url,
    description: channel.description_short,
  })) ?? []) as TaggingToolEntryBase[];

  return manualChannelEntries
    .map((taggingToolEntry) =>
      mapToManualEntryCard(taggingToolEntry, manualEntries),
    )
    .filter(notNull);
};

const getContentfulManualEntryCards = async (
  manualEntries: ManualEntry[],
  sdk: EditorExtensionSDK,
): Promise<ManualEntryCard[]> =>
  manualEntries.length === 0
    ? []
    : Promise.all(
        manualEntries.map((entry) => getContentfulManualEntryCard(entry, sdk)),
      );

const getRestManualEntryCards = (
  manualEntries: ManualEntry[],
): ManualEntryCard[] =>
  manualEntries.length === 0
    ? []
    : manualEntries.map((entry) => ({
        id: cryptoRandomString({ length: 10, type: 'url-safe' }),
        entryId: entry.entryId,
        type: entry.type,
        typeDisplayName: ENTRY_TYPES_DISPLAY_NAMES[entry.type],
        title: entry.type,
        description: entry.entryId,
        position: entry.position,
      }));

const TAGGING_TOOL_KEY = 'taggingTool';
const CMS_KEY = 'cms';
const CHANNEL_KEY = 'channel';
const REST_KEY = 'rest';

const getManualEntryCards = async (
  sdk: EditorExtensionSDK,
  initialManualEntries: ManualEntry[],
): Promise<ManualEntryCard[]> => {
  if (initialManualEntries.length === 0) {
    return [];
  }

  const getKey = (entry: ManualEntry) => {
    if (isTaggingToolEntryType(entry.type)) {
      return TAGGING_TOOL_KEY;
    }

    if (isCmsEntryType(entry.type)) {
      return CMS_KEY;
    }

    if (entry.type === CHANNEL_KEY) {
      return CHANNEL_KEY;
    }

    return REST_KEY;
  };

  const entriesBySource: Partial<
    Record<string, ManualEntry[]>
  > = R.groupBy<ManualEntry>(getKey)(initialManualEntries);

  const taggingToolManualEntryCards = await getTaggingToolManualEntryCards(
    entriesBySource[TAGGING_TOOL_KEY] ?? [],
  );

  const channelManualEntryCards = await getChannelManualEntryCards(
    entriesBySource[CHANNEL_KEY] ?? [],
  );

  const contentfulManualEntryCards = await getContentfulManualEntryCards(
    entriesBySource[CMS_KEY] ?? [],
    sdk,
  );

  const restManualEntryCards = getRestManualEntryCards(
    entriesBySource[REST_KEY] ?? [],
  );

  const manualEntryCardsByEntryId = [
    ...taggingToolManualEntryCards,
    ...channelManualEntryCards,
    ...contentfulManualEntryCards,
    ...restManualEntryCards,
  ].reduce((acc, item) => {
    acc[String(item.entryId)] = item;

    return acc;
  }, {} as Record<string, ManualEntryCard>);

  return initialManualEntries
    .map(
      (entry) =>
        manualEntryCardsByEntryId[String(entry.entryId)] ??
        mapToFallbackManualEntryCard(entry),
    )
    .filter(notNull);
};

export default getManualEntryCards;
